import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { HERO } from '@/constants/testIds';
import { HERO_IMAGE, COMPANY } from '@/data/site';
import { ArrowDown } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

const line = {
  hidden: { y: '110%' },
  show: (i = 0) => ({ y: '0%', transition: { duration: 1.1, ease: EASE, delay: 0.15 + i * 0.11 } }),
};

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);

  return (
    <section ref={ref} data-testid={HERO.section} className="relative min-h-screen w-full overflow-hidden bg-[#FAFAF5]">
      <div className="absolute inset-0 dot-grid opacity-70 -z-20" />

      <div className="absolute top-24 left-6 md:left-10 font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#0A0A0A]/70 flex items-center gap-2 z-10">
        <span className="w-8 h-px bg-[#DC2626]" /> Established 2023 · Se Habla Español
      </div>
      <div className="absolute top-24 right-6 md:right-10 font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#0A0A0A]/70 hidden md:flex items-center gap-2 z-10">
        {COMPANY.serviceArea} <span className="w-8 h-px bg-[#DC2626]" />
      </div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-40 pb-24 min-h-screen grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <motion.div
            initial="hidden"
            animate="show"
            className="font-mono-label text-[11px] tracking-[0.3em] uppercase text-[#DC2626] mb-8"
          >
            <span className="mask-line"><motion.span variants={line} custom={0} className="block">// Chapter 001 — Build with intent</motion.span></span>
          </motion.div>

          <h1 className="font-display uppercase text-[#0A0A0A] leading-[0.92] tracking-[-0.03em] text-[11vw] md:text-[8vw] lg:text-[6.6vw]">
            <span className="mask-line">
              <motion.span initial="hidden" animate="show" variants={line} custom={1} className="block">Quality <span className="text-[#1D4ED8]">Craftsmanship</span>.</motion.span>
            </span>
            <span className="mask-line">
              <motion.span initial="hidden" animate="show" variants={line} custom={2} className="block">Reliable Service.</motion.span>
            </span>
            <span className="mask-line">
              <motion.span initial="hidden" animate="show" variants={line} custom={3} className="block">
                Built to <span className="relative inline-block">last<span className="absolute -right-3 top-2 md:top-4 w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#DC2626]" /></span>.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 max-w-xl text-[#0A0A0A]/70 text-base md:text-lg leading-relaxed"
          >
            Alpha Tex is a residential and commercial construction studio serving the {COMPANY.serviceArea}. Fixed timelines. Named project managers. Craftsmanship that reads on camera and holds up in person.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link
              to="/contact"
              data-testid={HERO.ctaPrimary}
              className="group inline-flex items-center gap-3 bg-[#1D4ED8] text-white px-7 py-4 font-mono-label text-xs tracking-[0.24em] uppercase hover:bg-[#1E3A8A] transition-colors"
            >
              Request a Free Estimate
              <span className="w-6 h-px bg-white group-hover:w-10 transition-[width] duration-500" />
            </Link>
            <Link
              to="/projects"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center gap-3 border border-[#0A0A0A]/20 text-[#0A0A0A] px-7 py-4 font-mono-label text-xs tracking-[0.24em] uppercase hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              See the work →
            </Link>
          </motion.div>
        </div>

        <motion.div
          style={{ y: bgY, scale: bgScale }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.3 }}
          className="lg:col-span-4 relative aspect-[3/4] w-full overflow-hidden clip-frame border border-[#0A0A0A]/10"
        >
          <img src={HERO_IMAGE} alt="Alpha Tex construction" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 font-mono-label text-[10px] tracking-[0.24em] uppercase text-white bg-[#1D4ED8] px-2.5 py-1">
            Now Building
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
            <div className="font-mono-label text-[10px] tracking-[0.22em] uppercase">Boerne Custom Home · 2026</div>
            <div className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" />
          </div>
        </motion.div>
      </motion.div>

      <div className="relative z-10 border-t border-[#0A0A0A]/10 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid grid-cols-3 gap-8 md:gap-14">
          {[
            { k: COMPANY.years + '+', v: 'Years building' },
            { k: COMPANY.projects + '+', v: 'Projects delivered' },
            { k: COMPANY.rating + '★', v: 'Google rating' },
          ].map((s) => (
            <div key={s.v} className="flex items-baseline gap-4">
              <div className="font-display text-[#0A0A0A] text-4xl md:text-6xl leading-none">{s.k}</div>
              <div className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#0A0A0A]/60">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid={HERO.scroll} className="absolute top-4 md:top-6 right-6 md:right-10 font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#0A0A0A]/50 hidden md:flex items-center gap-2 z-10">
        <ArrowDown className="w-3.5 h-3.5" /> Scroll
      </div>
    </section>
  );
}
